import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, User, MessageSquare } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // EmailJS Configuration
      const serviceId = 'service_058dckb';
      const templateId = 'template_gceg5dd';
      const publicKey = '7kd227S-49V0-k1iA';

      // Template parameters - match exactly what's in EmailJS template
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        reply_to: formData.email,
        to_name: 'Ihsan',
        message: `
New Contact Form Submission

Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Service Interested In: ${formData.service || 'Not specified'}

Message:
${formData.message || 'No message provided'}

---
Sent from aiperformance.solutions contact form
        `.trim(),
      };

      // Send email - publicKey as 4th parameter
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      alert('Thank you for your inquiry! Ihsan will contact you within 24 hours.');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error: any) {
      console.error('EmailJS Error:', error);
      console.error('Error details:', {
        status: error?.status,
        text: error?.text,
        message: error?.message
      });
      alert(`Failed to send message. Error: ${error?.text || error?.message || 'Unknown error'}. Please contact directly via email: info@aiperformance.solutions`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="booking" className="relative py-20 overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-dark"></div>
      
      <div className="relative container mx-auto px-6">
        <h2 className="font-bebas text-5xl md:text-7xl text-center text-white mb-4 tracking-wider">
          CONTACT <span className="text-primary">ME</span>
        </h2>
        
        <p className="text-center text-white font-bebas text-2xl mb-12">
          WRITE OR CALL ME IF YOU HAVE MORE QUESTIONS
        </p>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="font-bebas text-3xl text-primary mb-6 tracking-wider">
                GET IN TOUCH
              </h3>
              <p className="text-gray-300 font-montserrat leading-relaxed mb-8">
                Ready to transform your fitness journey? Whether you're a complete beginner or 
                a seasoned athlete, I'm here to help you achieve your goals. Reach out today 
                and let's start building a stronger, healthier you.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start">
                <Mail className="text-primary mr-4 mt-1" size={24} />
                <div>
                  <h4 className="text-white font-montserrat font-semibold mb-1">Email</h4>
                  <a href="mailto:info@aiperformance.solutions" className="text-gray-400 hover:text-primary transition-colors">
                    info@aiperformance.solutions
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="text-primary mr-4 mt-1" size={24} />
                <div>
                  <h4 className="text-white font-montserrat font-semibold mb-1">Phone</h4>
                  <a href="tel:415-916-2448" className="text-gray-400 hover:text-primary transition-colors">
                    (415) 916-2448
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <MapPin className="text-primary mr-4 mt-1" size={24} />
                <div>
                  <h4 className="text-white font-montserrat font-semibold mb-1">Studio Location</h4>
                  <p className="text-gray-400">
                    1019 El Camino Real<br />
                    Menlo Park, CA 94025
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Response Promise */}
            <div className="bg-black/50 backdrop-blur-sm p-6">
              <Calendar className="text-primary mb-3" size={32} />
              <h4 className="text-white font-bebas text-xl mb-2">24-HOUR RESPONSE</h4>
              <p className="text-gray-400 font-montserrat text-sm">
                I personally respond to all inquiries within 24 hours. 
                Your fitness journey is my priority.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-black/50 backdrop-blur-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors"
                    placeholder="John"
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                  Service Interested In
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors"
                >
                  <option value="">Select a service</option>
                  <option value="strength">Strength & Conditioning</option>
                  <option value="wellness">Health & Wellness Coaching</option>
                  <option value="trial">Trial Session</option>
                  <option value="12pack">12-Pack Sessions</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-gray-400 font-montserrat text-sm mb-2 block">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-black/50 border border-gray-700 text-white px-4 py-3 focus:border-primary focus:outline-none transition-colors resize-none"
                  placeholder="Tell me about your fitness goals..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-red-600 text-white font-montserrat font-bold py-4 tracking-wider transition-all duration-300 transform hover:scale-105 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <Send className="mr-2" size={20} />
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;